import type { AuthResponse } from '../../entities/user/types';
import { apiFetch, jsonBody } from '../../shared/api/httpClient';
import { clientHeaders } from '../../shared/lib/device';
import { useAuthStore } from './authStore';
import { writeAuthPrefs } from './authShared';

const KEY = 'sorteia:google:pending';
export type GoogleSignup = { countryCode: string; locale: string; invite_code: string; accepted_terms: boolean; accepted_marketing: boolean; turnstileToken: string };
type Pending = { nonce: string; verifier: string; options: GoogleSignup; remember: boolean; expiresAt: number };
function base64(bytes: Uint8Array) { return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, ''); }
export async function prepareProof() {
  const verifier = base64(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = base64(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
  return { verifier, challenge };
}
function readPending(state: string): Pending {
  const pending: Pending = JSON.parse(sessionStorage.getItem(KEY) || 'null');
  if (!pending || pending.nonce !== state || pending.expiresAt < Date.now()) throw new Error('GOOGLE_INVALID_CREDENTIAL');
  return pending;
}
export function googleReturnLocale(state: string): string | undefined {
  try { return readPending(state).options.locale; } catch { return undefined; }
}
const exchanges = new Map<string, Promise<void>>();
export function exchangeGoogleCode(code: string, state: string): Promise<void> {
  const existing = exchanges.get(state);
  if (existing) return existing;
  const exchange = (async () => {
    const pending = readPending(state);
    try {
      const data = await apiFetch<AuthResponse>('/api/auth/google', {
        method: 'POST', headers: clientHeaders(),
        body: jsonBody({ ...pending.options, credential: code, nonce: state, codeVerifier: pending.verifier }),
      });
      writeAuthPrefs(data.user?.email || '', pending.remember);
      useAuthStore.getState().setSession({ token: data.token, expiresAt: Number(data.expires_at ?? data.expiresAt ?? 0), sessionSecret: data.sessionSecret, user: data.user, remember: pending.remember });
    } finally { sessionStorage.removeItem(KEY); }
  })();
  exchanges.set(state, exchange);
  return exchange;
}

export async function beginGoogleBridge(proof: Awaited<ReturnType<typeof prepareProof>>, options: GoogleSignup, remember: boolean, authOrigin: string, signal: AbortSignal): Promise<boolean> {
  // Open synchronously in the user click; full-page navigation also supports PWA / blocked popups.
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone;
  const popup = standalone ? null : window.open('', 'sorteia-google-login', 'popup=yes,width=480,height=720');
  try {
    const start = await apiFetch<{ nonce: string; authorizeUrl: string }>('/api/auth/google/challenge', {
      method: 'POST', signal, body: jsonBody({ codeChallenge: proof.challenge, turnstileToken: options.turnstileToken }),
    });
    const destination = new URL(start.authorizeUrl);
    if (destination.origin !== authOrigin || destination.pathname !== '/auth/google') throw new Error('GOOGLE_INVALID_CREDENTIAL');
    const fragment = new URLSearchParams(destination.hash.slice(1));
    fragment.set('locale', options.locale);
    destination.hash = fragment.toString();
    const pending: Pending = { nonce: start.nonce, verifier: proof.verifier, options: { ...options, turnstileToken: '' }, remember, expiresAt: Date.now() + 600000 };
    const serialized = JSON.stringify(pending);
    sessionStorage.setItem(KEY, serialized);
    if (!popup) { window.location.assign(destination.href); return false; }
    // A copy allows the callback to finish in the popup if COOP severs its opener.
    popup.sessionStorage.setItem(KEY, serialized);
    const code = await new Promise<string>((resolve, reject) => {
      const cleanup = () => { window.removeEventListener('message', receive); signal.removeEventListener('abort', abort); window.clearInterval(timer); };
      const abort = () => { cleanup(); reject(new Error('GOOGLE_CANCELLED')); };
      const receive = (event: MessageEvent) => {
        if (event.origin !== window.location.origin || event.source !== popup || event.data?.type !== 'sorteia:google-result' || event.data.state !== start.nonce || typeof event.data.code !== 'string') return;
        popup.postMessage({ type: 'sorteia:google-ack', state: start.nonce }, window.location.origin);
        cleanup(); resolve(event.data.code);
      };
      const timer = window.setInterval(() => { if (popup.closed || pending.expiresAt < Date.now()) { cleanup(); reject(new Error('GOOGLE_CANCELLED')); } }, 500);
      window.addEventListener('message', receive);
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) { abort(); return; }
      popup.location.replace(destination.href);
    });
    await exchangeGoogleCode(code, start.nonce);
    return true;
  } catch (error) { sessionStorage.removeItem(KEY); throw error; }
  finally { if (popup && !popup.closed) popup.close(); }
}

export function deliverGoogleResult(code: string, state: string): Promise<boolean> {
  readPending(state);
  if (!window.opener) return Promise.resolve(false);
  return new Promise(resolve => {
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== window.opener || event.data?.type !== 'sorteia:google-ack' || event.data.state !== state) return;
      clearTimeout(timer); window.removeEventListener('message', receive); resolve(true);
    };
    const timer = window.setTimeout(() => { window.removeEventListener('message', receive); resolve(false); }, 1500);
    window.addEventListener('message', receive);
    window.opener.postMessage({ type: 'sorteia:google-result', code, state }, window.location.origin);
  });
}
